import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  InputAdornment,
  Grid,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { UniversalPagination } from '../../components/common/UniversalPagination';
import {
  metActivityService,
  MetActivity,
  MetActivityOptions,
  MetCompendiumType,
  MetSortBy,
  GetMetActivitiesParams,
  CreateMetActivityDto,
} from '../../services/met-activity.service';
import { Locale } from '../../services/workout.service';
import { useDebounce } from '../../hooks/useDebounce';
import { toastService } from '../../services/toast.service';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

const LOCALES: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

const createEmptyForm = () => ({
  code: '',
  compendiumType: 'adult' as MetCompendiumType,
  categoryCode: '' as number | '',
  categoryName: '',
  nameEn: '',
  nameI18n: LOCALES.reduce((acc, loc) => ({ ...acc, [loc]: '' }), {} as Record<Locale, string>),
  metValue: '' as number | '',
  youthAgeGroup: '' as number | '',
});

export const MetActivitiesTab: React.FC = () => {
  const [activities, setActivities] = useState<MetActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(20);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [compendiumTypeFilter, setCompendiumTypeFilter] = useState<MetCompendiumType | ''>('');
  const [categoryCodeFilter, setCategoryCodeFilter] = useState<number | ''>('');

  const [sortBy, setSortBy] = useState<MetSortBy>('id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [options, setOptions] = useState<MetActivityOptions>({ compendiumTypes: [], categories: [], youthAgeGroups: [], sourceTypes: [] });

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isEditOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState(createEmptyForm());
  const [formLoading, setFormLoading] = useState(false);

  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<MetActivity | null>(null);

  useEffect(() => {
    metActivityService.getOptions().then(setOptions).catch(() => {
      toastService.error('Не вдалося завантажити параметри фільтрів.');
    });
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetMetActivitiesParams = {
        page,
        limit,
        search: debouncedSearch || undefined,
        compendiumType: compendiumTypeFilter || undefined,
        categoryCode: categoryCodeFilter !== '' ? (categoryCodeFilter as number) : undefined,
        sortBy,
        sortOrder,
      };
      const response = await metActivityService.getActivities(params);
      setActivities(response.data);
      setTotalPages(
        response.meta?.total && response.meta?.limit
          ? Math.ceil(response.meta.total / response.meta.limit)
          : 0
      );
    } catch {
      toastService.error('Не вдалося завантажити активності.');
      setError('Не вдалося завантажити активності.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, compendiumTypeFilter, categoryCodeFilter, sortBy, sortOrder]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleSort = (property: MetSortBy) => {
    const isAsc = sortBy === property && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setCompendiumTypeFilter('');
    setCategoryCodeFilter('');
    setPage(1);
    setSortBy('id');
    setSortOrder('ASC');
  };

  const handleOpenCreate = () => {
    setFormData(createEmptyForm());
    setCreateOpen(true);
  };

  const handleOpenEdit = async (activity: MetActivity) => {
    setEditingId(activity.id);
    setEditOpen(true);
    setFormLoading(true);
    try {
      const data = await metActivityService.getActivityForEdit(activity.id);
      setFormData({
        code: data.code,
        compendiumType: data.compendiumType,
        categoryCode: data.categoryCode,
        categoryName: data.categoryName,
        nameEn: data.nameEn,
        nameI18n: LOCALES.reduce(
          (acc, loc) => ({ ...acc, [loc]: data.nameI18n?.[loc] || '' }),
          {} as Record<Locale, string>
        ),
        metValue: data.metValue,
        youthAgeGroup: data.youthAgeGroup ?? '',
      });
    } catch {
      toastService.error('Не вдалося завантажити дані для редагування.');
      setEditOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const buildPayload = (): CreateMetActivityDto | null => {
    if (!formData.code.trim()) { toastService.error("Код є обов'язковим."); return null; }
    if (formData.categoryCode === '') { toastService.error("Код категорії є обов'язковим."); return null; }
    if (!formData.categoryName.trim()) { toastService.error("Назва категорії є обов'язковою."); return null; }
    if (!formData.nameEn.trim()) { toastService.error("Англійська назва є обов'язковою."); return null; }
    if (formData.metValue === '') { toastService.error("MET є обов'язковим."); return null; }
    if (formData.compendiumType === 'youth' && formData.youthAgeGroup === '') {
      toastService.error("Вікова група є обов'язковою для Youth.");
      return null;
    }

    const nameI18n = Object.fromEntries(
      Object.entries(formData.nameI18n).filter(([, v]) => v.trim() !== '')
    ) as { [key in Locale]?: string };

    return {
      code: formData.code.trim(),
      compendiumType: formData.compendiumType,
      categoryCode: formData.categoryCode as number,
      categoryName: formData.categoryName.trim(),
      nameEn: formData.nameEn.trim(),
      nameI18n: Object.keys(nameI18n).length > 0 ? nameI18n : undefined,
      metValue: formData.metValue as number,
      ...(formData.compendiumType === 'youth' && formData.youthAgeGroup !== ''
        ? { youthAgeGroup: formData.youthAgeGroup as number }
        : {}),
    };
  };

  const handleCreate = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setFormLoading(true);
    try {
      await metActivityService.createActivity(payload);
      toastService.success('Активність успішно створена!');
      setCreateOpen(false);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка: ${err.message || 'Невідома помилка'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const payload = buildPayload();
    if (!payload) return;
    setFormLoading(true);
    try {
      await metActivityService.updateActivity(editingId, payload);
      toastService.success('Активність успішно оновлена!');
      setEditOpen(false);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка: ${err.message || 'Невідома помилка'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!activityToDelete) return;
    setLoading(true);
    try {
      await metActivityService.deleteActivity(activityToDelete.id);
      toastService.success(`Активність "${activityToDelete.nameEn}" видалена.`);
      setDeleteOpen(false);
      setActivityToDelete(null);
      fetchActivities();
    } catch (err: any) {
      toastService.error(`Помилка видалення: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field: string, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const updateNameI18n = (locale: Locale, value: string) =>
    setFormData(prev => ({ ...prev, nameI18n: { ...prev.nameI18n, [locale]: value } }));

  const renderSortLabel = (id: MetSortBy, label: string) => (
    <TableSortLabel
      active={sortBy === id}
      direction={sortBy === id ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
      onClick={() => handleSort(id)}
    >
      {label}
    </TableSortLabel>
  );

  const renderForm = () => (
    <Box sx={{ pt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth margin="dense" label="Код" value={formData.code}
            onChange={e => updateForm('code', e.target.value)}
            required inputProps={{ maxLength: 6 }}
            helperText="До 6 символів"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth margin="dense">
            <InputLabel>Компендіум</InputLabel>
            <Select
              value={formData.compendiumType}
              label="Компендіум"
              onChange={e => updateForm('compendiumType', e.target.value)}
            >
              {options.compendiumTypes.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth margin="dense" label="Код категорії" type="number"
            value={formData.categoryCode}
            onChange={e => updateForm('categoryCode', e.target.value !== '' ? Number(e.target.value) : '')}
            required inputProps={{ min: 1, max: 99 }}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth margin="dense" label="Назва категорії" value={formData.categoryName}
            onChange={e => updateForm('categoryName', e.target.value)}
            required inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth margin="dense" label="Назва (English, оригінал)" value={formData.nameEn}
            onChange={e => updateForm('nameEn', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth margin="dense" label="MET" type="number"
            value={formData.metValue}
            onChange={e => updateForm('metValue', e.target.value !== '' ? Number(e.target.value) : '')}
            required inputProps={{ min: 0.1, step: 0.1 }}
          />
        </Grid>
        {formData.compendiumType === 'youth' && (
          <Grid item xs={12}>
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Вікова група</InputLabel>
              <Select
                value={formData.youthAgeGroup}
                label="Вікова група"
                onChange={e => updateForm('youthAgeGroup', e.target.value)}
              >
                {options.youthAgeGroups.map(g => (
                  <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Переклади назви (опціонально)
          </Typography>
        </Grid>
        {LOCALES.map(loc => (
          <Grid item xs={6} key={loc}>
            <TextField
              fullWidth margin="dense" size="small"
              label={`Назва (${loc.toUpperCase()})`}
              value={formData.nameI18n[loc] || ''}
              onChange={e => updateNameI18n(loc, e.target.value)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled>
          Додати активність
        </Button>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small" label="Пошук за назвою"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Компендіум</InputLabel>
              <Select
                value={compendiumTypeFilter}
                label="Компендіум"
                onChange={(e: SelectChangeEvent) => {
                  setCompendiumTypeFilter(e.target.value as MetCompendiumType | '');
                  setPage(1);
                }}
              >
                <MenuItem value=""><em>Всі</em></MenuItem>
                {options.compendiumTypes.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Категорія</InputLabel>
              <Select
                value={categoryCodeFilter === '' ? '' : String(categoryCodeFilter)}
                label="Категорія"
                onChange={(e: SelectChangeEvent) => {
                  setCategoryCodeFilter(e.target.value !== '' ? Number(e.target.value) : '');
                  setPage(1);
                }}
              >
                <MenuItem value=""><em>Всі</em></MenuItem>
                {options.categories.map(c => (
                  <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="outlined" color="secondary" fullWidth onClick={handleResetFilters}>
              Скинути
            </Button>
          </Grid>
        </Grid>

        {error ? (
          <Typography color="error" textAlign="center" sx={{ mt: 4 }}>{error}</Typography>
        ) : loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>
        ) : activities.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Активностей не знайдено.</Typography>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{renderSortLabel('code', 'Код')}</TableCell>
                    <TableCell>Компендіум</TableCell>
                    <TableCell>{renderSortLabel('categoryCode', 'Категорія')}</TableCell>
                    <TableCell>{renderSortLabel('nameEn', 'Назва (EN)')}</TableCell>
                    <TableCell>Назва (локаль)</TableCell>
                    <TableCell>{renderSortLabel('metValue', 'MET')}</TableCell>
                    <TableCell align="right">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.map(activity => (
                    <TableRow hover key={activity.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{activity.code}</TableCell>
                      <TableCell>{activity.compendiumType}</TableCell>
                      <TableCell>{activity.categoryName}</TableCell>
                      <TableCell>{activity.nameEn}</TableCell>
                      <TableCell>{activity.name !== activity.nameEn ? activity.name : '—'}</TableCell>
                      <TableCell>{activity.metValue}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpenEdit(activity)} size="small" disabled>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => { setActivityToDelete(activity); setDeleteOpen(true); }}
                          size="small"
                          disabled
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <UniversalPagination page={page} count={totalPages} limit={limit} onPageChange={(v) => setPage(v)} onLimitChange={(v) => { setLimit(v); setPage(1); }} storageKey="pagination:library-met-activities" limitOptions={[10, 20, 50]} />
          </>
        )}
      </Paper>

      <Dialog open={isCreateOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Додати активність</DialogTitle>
        <DialogContent>{renderForm()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Скасувати</Button>
          <Button onClick={handleCreate} disabled={formLoading} variant="contained">
            {formLoading ? <CircularProgress size={24} /> : 'Створити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Редагувати активність</DialogTitle>
        <DialogContent>
          {formLoading
            ? <Box display="flex" justifyContent="center" sx={{ py: 4 }}><CircularProgress /></Box>
            : renderForm()
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Скасувати</Button>
          <Button onClick={handleUpdate} disabled={formLoading} variant="contained">
            {formLoading ? <CircularProgress size={24} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={isDeleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Підтвердити видалення"
        message={`Ви впевнені, що хочете видалити активність "${activityToDelete?.nameEn}"?`}
        confirmText="Видалити"
      />
    </Box>
  );
};
